import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { farmbaseApi, platoonApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import PhaseChip from './PhaseChip';
import {
  ROTATION_LANES,
  laneForPhase,
  type Farmbase,
  type Platoon,
  type RotationLane,
} from '../types/domain';

// Per-farmbase 3-lane rotation board (dashboard spec §4). Read-only: phase changes
// happen through PhaseControl. Warns (amber) when a lane is empty or double-occupied
// — the operator signal that the staggered rotation has slipped.
export default function RotationBoard({
  unitIds,
  className,
}: {
  unitIds: string[] | null; // null = all in-scope units (HQ)
  className?: string;
}) {
  const { units } = useUnitStore();

  const { data: farmbaseData } = useQuery({
    queryKey: ['rotation-farmbases', unitIds],
    queryFn: () => farmbaseApi.getAll(unitIds && unitIds.length === 1 ? { unitId: unitIds[0] } : undefined),
    refetchInterval: 8000,
  });
  const { data: platoonData } = useQuery({
    queryKey: ['rotation-platoons', unitIds],
    queryFn: () => platoonApi.getAll(unitIds && unitIds.length === 1 ? { unitId: unitIds[0] } : undefined),
    refetchInterval: 5000,
  });

  const farmbases: Farmbase[] = useMemo(() => {
    let list: Farmbase[] = farmbaseData?.data?.farmbases || [];
    if (unitIds) list = list.filter((f) => unitIds.includes(f.unitId));
    return list;
  }, [farmbaseData, unitIds]);

  const platoons: Platoon[] = useMemo(() => {
    let list: Platoon[] = platoonData?.data?.platoons || [];
    if (unitIds) list = list.filter((p) => unitIds.includes(p.unitId));
    return list;
  }, [platoonData, unitIds]);

  const unitName = (unitId: string) => units.find((u) => u.id === unitId)?.name || 'Unit';

  if (farmbases.length === 0) {
    return (
      <div className={clsx('rounded-xl border border-command-border bg-command-panel p-5', className)}>
        <h3 className="text-base font-semibold text-gray-100 mb-1">Rotation Board</h3>
        <p className="text-gray-500 text-sm">No farmbases in view yet.</p>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-3', className)}>
      <h3 className="text-base font-semibold text-gray-100">Rotation Board</h3>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {farmbases.map((fb) => {
          const fbPlatoons = platoons.filter((p) => p.farmbaseId === fb.id);
          const recovered = fbPlatoons.filter((p) => p.currentPhase === 'RECOVERED');
          const buckets: Record<RotationLane, Platoon[]> = { SECURITY: [], RECCE: [], EXECUTION: [] };
          fbPlatoons.forEach((p) => {
            const lane = laneForPhase(p.currentPhase);
            if (lane) buckets[lane].push(p);
          });

          return (
            <div key={fb.id} className="rounded-xl border border-command-border bg-command-panel p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-gray-100">{fb.name}</div>
                  <div className="text-[10px] text-gray-500 font-tactical uppercase">{unitName(fb.unitId)}</div>
                </div>
                <span className="text-[10px] text-gray-500">{fbPlatoons.length} platoons</span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {ROTATION_LANES.map((lane) => {
                  const inLane = buckets[lane.lane];
                  const slipped = inLane.length !== 1;
                  return (
                    <div
                      key={lane.lane}
                      className={clsx(
                        'rounded-lg border p-2 min-h-[92px]',
                        slipped ? 'border-amber-500/60 bg-amber-500/5' : 'border-command-border bg-command-bg/40'
                      )}
                    >
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
                        {lane.label}
                        {slipped && (
                          <span className="ml-1 text-amber-400" title="Rotation slippage: lane empty or double-occupied">
                            ⚠
                          </span>
                        )}
                      </div>
                      {inLane.length === 0 ? (
                        <div className="text-[10px] text-gray-600 italic">empty</div>
                      ) : (
                        inLane.map((p) => <PlatoonCell key={p.id} platoon={p} />)
                      )}
                    </div>
                  );
                })}
              </div>

              {recovered.length > 0 && (
                <div className="mt-2 pt-2 border-t border-command-border/60">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Cycle complete</div>
                  <div className="flex flex-wrap gap-2">
                    {recovered.map((p) => (
                      <span key={p.id} className="text-[11px] text-gray-500">
                        {p.callsign}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlatoonCell({ platoon }: { platoon: Platoon }) {
  const lastSeen = platoon.device?.lastSeenAt;
  return (
    <div className="mb-1.5 last:mb-0">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-gray-100">{platoon.callsign}</span>
        {platoon.sos && (
          <span className="px-1 rounded text-[9px] font-bold bg-red-500 text-white animate-pulse">SOS</span>
        )}
      </div>
      <div className="mt-0.5">
        <PhaseChip phase={platoon.currentPhase} size="xs" />
      </div>
      <div className="text-[9px] text-gray-500 mt-0.5">
        {platoon.commanderName || '—'}
        {platoon.strength != null && ` · ${platoon.strength}`}
      </div>
      {lastSeen && (
        <div className="text-[9px] text-gray-600">{formatDistanceToNow(new Date(lastSeen), { addSuffix: true })}</div>
      )}
    </div>
  );
}
